'use-client';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
  forwardRef,
  memo,
} from 'react';
import type { MapMouseEvent } from '@vis.gl/react-google-maps';
import { useMap, APIProvider, Map } from '@vis.gl/react-google-maps';
import type {
  GoogleMapProps,
  IGoogleMapsImperativeActions,
} from '../GoogleMap.types';
import GoogleMapMarker from './GoogleMapMarker';
import { createImperativeMethods } from './utils/imperativeMethods';
import { isValidZoom } from './utils/utils';
import { API_KEY, DEFAULT_ZOOM, MAP_IDS, MAP_TYPES } from '../constants';
import { hasMarkerInfoWindowFields } from '../utils';

const GoogleMapReactInner = forwardRef<
  IGoogleMapsImperativeActions,
  GoogleMapProps
>((props, forwardedRef) => {
  const {
    id,
    mapData,
    translations,
    onUpdateCenter,
    onUpdateZoom,
    onMarkerClicked,
    onMapClicked,
    onMapMount,
  } = props;

  const map = useMap();
  const mapRef = useRef<google.maps.Map | null>(map);

  useEffect(() => {
    // On initial render, the map is null, so we need to set the ref to the map
    mapRef.current = map;
  }, [map]);

  const defaultZoom = useMemo(() => {
    return (isValidZoom(mapData.zoom) && mapData.zoom) || DEFAULT_ZOOM;
  }, [mapData.zoom]);
  useEffect(() => map?.setZoom(defaultZoom), [defaultZoom, map]);

  const defaultLocationIndex = useMemo(() => {
    if (
      mapData.defaultLocation >= 0 &&
      mapData.locations &&
      mapData.defaultLocation < mapData.locations.length
    ) {
      return mapData.defaultLocation;
    }
    return 0;
  }, [mapData.locations, mapData.defaultLocation]);

  const defaultCenter = useMemo(() => {
    if (!mapData.locations || mapData.locations.length === 0) {
      return { lat: 0, lng: 0 };
    }

    const location = mapData.locations[defaultLocationIndex];

    return {
      lat: location?.latitude ?? 0,
      lng: location?.longitude ?? 0,
    };
  }, [mapData.locations, defaultLocationIndex]);
  useEffect(() => map?.setCenter(defaultCenter), [defaultCenter, map]);

  const [openInfoWindowIndex, setOpenInfoWindowIndex] = useState<number | null>(
    defaultLocationIndex,
  );
  useEffect(
    () => setOpenInfoWindowIndex(defaultLocationIndex),
    [defaultLocationIndex],
  );

  const handleZoomChange = useCallback(
    (zoom: number) => {
      if (isValidZoom(zoom)) {
        onUpdateZoom?.({ zoom });
      }
    },
    [onUpdateZoom],
  );

  const handleCenterChanged = useCallback(
    (center: google.maps.LatLngLiteral) => {
      if (center) {
        onUpdateCenter?.({ latitude: center.lat, longitude: center.lng });
      }
    },
    [onUpdateCenter],
  );

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      try {
        if (onMapClicked && event.detail.latLng) {
          onMapClicked({
            type: 'mapClicked',
            location: {
              latitude: event.detail.latLng.lat,
              longitude: event.detail.latLng.lng,
            },
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('GoogleMap: Click handler error', {
          error: error instanceof Error ? error.message : String(error),
          componentId: id,
        });
      }
    },
    [onMapClicked, id],
  );

  const handleMarkerClicked = (index: number) => (data: any) => {
    setOpenInfoWindowIndex(openInfoWindowIndex === index ? null : index);
    onMarkerClicked?.(data);
  };

  useImperativeHandle(
    forwardedRef,
    () =>
      createImperativeMethods(
        mapRef,
        handleZoomChange,
        handleCenterChanged,
        mapData.locations,
      ),
    [handleZoomChange, handleCenterChanged, mapData.locations],
  );

  useEffect(() => {
    onMapMount?.();
  }, [onMapMount]);

  const mapStyleConfig = useMemo(() => {
    const styleKey = mapData.mapStyleKey as keyof typeof MAP_IDS;
    return {
      mapId: MAP_IDS[styleKey] || MAP_IDS.CLASSIC,
      mapTypeId: (MAP_TYPES[styleKey] || MAP_TYPES.CLASSIC).toLowerCase(),
    };
  }, [mapData.mapStyleKey]);

  const mapProps = useMemo(
    () => ({
      fullscreenControl: false,
      clickableIcons: true,
      keyboardShortcuts: true,
      disableDoubleClickZoom: false,
      disableDefaultUI: true,
      gestureHandling: mapData.mapInteractive ? 'auto' : 'none',
      mapTypeControl: mapData.showMapType,
      zoomControl: mapData.showZoom,
      streetViewControl: mapData.showStreetView,
    }),
    [
      mapData.mapInteractive,
      mapData.showMapType,
      mapData.showZoom,
      mapData.showStreetView,
    ],
  );

  return (
    <Map
      onCenterChanged={e => handleCenterChanged(e.detail.center)}
      onZoomChanged={e => handleZoomChange(e.detail.zoom)}
      onClick={handleMapClick}
      style={{ width: '100%', height: '100%' }}
      defaultCenter={defaultCenter}
      defaultZoom={defaultZoom}
      {...mapProps}
      {...mapStyleConfig}
    >
      {mapData.locations.map((location, index) => (
        <GoogleMapMarker
          key={`${index}-${location.latitude}-${location.longitude}`}
          location={location}
          infoWindowOpen={
            openInfoWindowIndex === index &&
            hasMarkerInfoWindowFields(location, mapData)
          }
          onMarkerClick={handleMarkerClicked(index)}
          onInfoWindowClose={() => setOpenInfoWindowIndex(null)}
          showDirectionLink={mapData.showDirectionsLink}
          allyTargetBlankDescription={translations.allyTargetBlankDescription}
        />
      ))}
    </Map>
  );
});

const GoogleMapReactInteractiveMap = forwardRef<
  IGoogleMapsImperativeActions,
  GoogleMapProps
>((props, forwardedRef) => {
  return (
    <APIProvider apiKey={API_KEY} language={props.language}>
      <GoogleMapReactInner {...props} ref={forwardedRef} />
    </APIProvider>
  );
});

export default memo(GoogleMapReactInteractiveMap);
